import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  X,
  FileText,
  FilePdf,
  FileDoc,
  FilePpt,
  FileXls,
  FileCsv,
  Globe,
  MagnifyingGlass,
  CheckCircle,
  WarningCircle,
  Clock,
  ArrowsClockwise,
  DownloadSimple,
  Trash,
  Robot,
  CaretDown,
  CaretUp,
  ArrowSquareOut,
  Tag as TagIcon,
  Database,
  File,
  Calendar,
  Hash,
  ChartBar,
  Sparkle,
  Quotes,
  Eye,
  StackSimple,
  Info,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { materialsApi, getErrorMessage } from '@/lib/api'
import { sanitizeMaterialSummary } from '@/lib/insights'
import { downloadMaterial } from '../downloadMaterial'
import { useScrollLock } from '@/lib/useScrollLock'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import { cn } from '@/lib/utils'
import { cardChromeClass } from '@/lib/styleKit'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MaterialDetail {
  id: number
  title: string
  description?: string
  material_type: string
  content_type: string
  file_name?: string
  file_path?: string
  file_size?: number
  url?: string
  processing_status: string
  created_at: string
  updated_at?: string
  usage_count: number
  tags?: string[]
  category?: string
  ai_summary?: string
  key_points?: string[]
  pageindex_doc_id?: string
  crawl_pages_found?: number
  extracted_text?: string
  extracted_metadata?: Record<string, any>
  topics?: string[]
  sentiment_analysis?: Record<string, any>
  competitive_insights?: Record<string, any>
  competitor_name?: string
  competitor_industry?: string
  processing_error?: string
  processed_at?: string
}

interface MaterialPreviewDrawerProps {
  materialId: number | null
  isOpen: boolean
  onClose: () => void
  onDelete: (id: number) => void
  onAnalyze: (id: number) => void
}

type TabId = 'content' | 'analysis' | 'metadata'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getContentTypeIcon(type: string): typeof File {
  switch (type) {
    case 'pdf': return FilePdf
    case 'docx': return FileDoc
    case 'pptx': return FilePpt
    case 'excel': return FileXls
    case 'csv': return FileCsv
    case 'website': return Globe
    case 'text': case 'markdown': return FileText
    default: return File
  }
}

const statusConfig: Record<string, { icon: typeof CheckCircle; color: string; label: string }> = {
  completed: { icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400', label: 'Completed' },
  processing: { icon: ArrowsClockwise, color: 'text-[#5e6ad2] dark:text-[#a5b4fc]', label: 'Processing...' },
  crawling:   { icon: ArrowsClockwise, color: 'text-[#5e6ad2] dark:text-[#a5b4fc]', label: 'Crawling website...' },
  indexing:   { icon: ArrowsClockwise, color: 'text-[#5e6ad2] dark:text-[#a5b4fc]', label: 'Indexing...' },
  failed:     { icon: WarningCircle,   color: 'text-red-600 dark:text-red-400', label: 'Failed' },
  pending:    { icon: Clock,           color: 'text-muted-foreground dark:text-[#8a8f98]', label: 'Pending' },
}

const EYEBROW = 'text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]'

// ---------------------------------------------------------------------------
// Content Tab
// ---------------------------------------------------------------------------

function ContentTab({ material }: { material: MaterialDetail }) {
  const [showExtractedText, setShowExtractedText] = useState(false)
  const [crawledUrlsOpen, setCrawledUrlsOpen] = useState(false)
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const canPreviewInline = ['pdf'].includes(material.content_type)
  const canPreviewSlides = ['pptx'].includes(material.content_type)
  // docx / txt / markdown / excel / csv have no browser-native inline renderer,
  // but the backend already extracts their full text into
  // material.extracted_text (routes/materials.py -> /{id}/detail). For excel/csv
  // that text is the columns, sample rows, and summary stats. Render that whole
  // content inline instead of the download-only card so the user can read it in
  // place.
  const canPreviewText = ['docx', 'text', 'markdown', 'excel', 'csv'].includes(material.content_type)
  const [slides, setSlides] = useState<any>(null)
  const [slidesLoading, setSlidesLoading] = useState(false)
  // Bumped by "Try again"; a non-zero value makes the next poll ask the backend
  // to re-render a previously failed deck (?retry=true). A plain drawer open
  // never re-renders a failed deck, so it stops re-running a broken conversion.
  const [slidesRetryNonce, setSlidesRetryNonce] = useState(0)
  const [currentSlide, setCurrentSlide] = useState(0)

  // Load slides for PPTX. The backend renders asynchronously into a
  // blob-storage cache (see app/tasks/materials.py:render_slides_task);
  // until that task finishes the endpoint returns status=processing
  // with an empty slides array. We poll until completed | failed.
  //
  // Slide rendering runs in parallel with the text/embedding pipeline
  // — the render task is dispatched right after upload, not after
  // base processing — so we DO NOT gate this on processing_status.
  // The user can see slides while text extraction is still running.
  //
  // Transient HTTP errors during polling don't kill the loop — we back
  // off and retry up to MAX_TRANSIENT_FAILS before surfacing a failed
  // state. That covers worker restarts, brief DB hiccups, etc.
  useEffect(() => {
    if (!canPreviewSlides || !material.file_path) return

    let cancelled = false
    let timer: number | undefined
    let transientFails = 0
    const MAX_TRANSIENT_FAILS = 5
    const POLL_INTERVAL_MS = 2500
    const BACKOFF_BASE_MS = 1500

    const poll = (withRetry = false) => {
      materialsApi.getSlides(material.id, withRetry).then((res) => {
        if (cancelled) return
        transientFails = 0
        const data = res.data
        setSlides(data)
        setCurrentSlide((c) => {
          const max = Math.max(0, (data.total_slides || 1) - 1)
          return Math.min(c, max)
        })
        const status = data?.status
        if (status === 'completed' || status === 'failed') {
          setSlidesLoading(false)
          return
        }
        // Still rendering — 2.5s is fast enough that the UI feels alive
        // and slow enough that a 60s render only costs ~25 polls.
        timer = window.setTimeout(poll, POLL_INTERVAL_MS)
      }).catch(() => {
        if (cancelled) return
        transientFails += 1
        if (transientFails >= MAX_TRANSIENT_FAILS) {
          setSlides({ status: 'failed', error: 'Could not reach the slides service' })
          setSlidesLoading(false)
          return
        }
        // Exponential backoff capped near 10s so a long outage doesn't
        // hammer the API but recovery is still quick once it's back.
        const delay = Math.min(BACKOFF_BASE_MS * 2 ** (transientFails - 1), 10000)
        timer = window.setTimeout(poll, delay)
      })
    }

    setSlidesLoading(true)
    setSlides(null)
    // Only the "Try again" path (nonce > 0) asks the backend to re-render a
    // failed deck; a normal open just reads the cached/last state.
    poll(slidesRetryNonce > 0)
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [material.id, canPreviewSlides, material.file_path, slidesRetryNonce])

  // Load preview blob for PDF files
  useEffect(() => {
    if (!canPreviewInline || !material.file_path) return
    let cancelled = false
    setPreviewLoading(true)
    materialsApi.getPreviewBlob(material.id).then((url) => {
      if (!cancelled) {
        setPreviewBlobUrl(url)
        setPreviewLoading(false)
      }
    }).catch(() => {
      if (!cancelled) setPreviewLoading(false)
    })
    return () => {
      cancelled = true
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl)
    }
  }, [material.id, canPreviewInline, material.file_path])
  const isWebsite = material.content_type === 'website'
  const text = material.extracted_text || ''

  const crawledUrls = material.extracted_metadata?.crawled_urls as string[] | undefined
  const hasCrawledUrls = !!crawledUrls && crawledUrls.length > 0

  // Slide rendering is independent of text extraction — for PPTX we
  // let the preview area render even while base processing is still
  // running so the user sees "Rendering slides..." instead of the
  // generic "Processing..." block. Other content types still show the
  // generic state until extraction completes.
  if (!text && material.processing_status !== 'completed' && !canPreviewSlides) {
    const cfg = statusConfig[material.processing_status] || statusConfig.pending
    const StatusIcon = cfg.icon
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
          <StatusIcon className={cn('h-5 w-5', cfg.color, material.processing_status !== 'failed' && material.processing_status !== 'pending' && 'animate-spin')} />
        </div>
        <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}>{cfg.label}</p>
        {material.processing_status === 'failed' ? (
          <p className="text-red-600 dark:text-red-400 mt-1 max-w-md" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
            {material.processing_error ||
              'This file could not be processed. It may be corrupted or unreadable. Please re-save it and upload again.'}
          </p>
        ) : (
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Content will appear here once processing completes.</p>
        )}
      </div>
    )
  }

  // Show extracted text view
  if (showExtractedText && text) {
    return <ExtractedTextView text={text} onBack={() => setShowExtractedText(false)} />
  }

  return (
    <div className="space-y-4">
      {/* Website crawl banner */}
      {isWebsite && material.crawl_pages_found != null && (
        <div className="rounded-lg border border-[#5e6ad2]/25 bg-[#5e6ad2]/[0.06] dark:border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.10] p-3.5">
          {/* Only a disclosure when there is a list to disclose — a crawl that
              found nothing renders the same row as static text, not a button
              that announces itself and then does nothing on activation. */}
          {(() => {
            const summary = (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                <span className="text-[#5e6ad2] dark:text-[#a5b4fc]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                  Crawled {material.crawl_pages_found} pages
                </span>
              </div>
            )
            return hasCrawledUrls ? (
              <button
                type="button"
                className="w-full flex items-center justify-between"
                onClick={() => setCrawledUrlsOpen(!crawledUrlsOpen)}
                aria-expanded={crawledUrlsOpen}
                aria-controls="crawled-urls-list"
              >
                {summary}
                {crawledUrlsOpen
                  ? <CaretUp className="h-4 w-4 text-[#5e6ad2]/70 dark:text-[#a5b4fc]/70" weight="bold" />
                  : <CaretDown className="h-4 w-4 text-[#5e6ad2]/70 dark:text-[#a5b4fc]/70" weight="bold" />}
              </button>
            ) : (
              <div className="w-full flex items-center justify-between">{summary}</div>
            )
          })()}
          {crawledUrlsOpen && hasCrawledUrls && (
            <div id="crawled-urls-list" className="mt-2.5 space-y-1.5 max-h-40 overflow-y-auto">
              {crawledUrls!.map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-[12px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline truncate transition-colors duration-150"
                >
                  <ArrowSquareOut className="h-3 w-3 flex-shrink-0" />
                  {url}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inline file preview (PDF) */}
      {canPreviewInline && (
        <div className="rounded-lg border border-border/50 dark:border-white/[0.08] overflow-hidden bg-white dark:bg-[#0f1011]">
          {previewLoading ? (
            <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 340px)', minHeight: '400px' }}>
              <LoadingSpinner size="lg" text="Loading preview..." />
            </div>
          ) : previewBlobUrl ? (
            <iframe
              src={previewBlobUrl}
              className="w-full border-0"
              style={{ height: 'calc(100vh - 340px)', minHeight: '400px' }}
              title={`Preview of ${material.title}`}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                <FileText className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
              </div>
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Preview not available</p>
            </div>
          )}
        </div>
      )}

      {/* PPTX Slide Preview */}
      {canPreviewSlides && (
        <div className="rounded-lg border border-border/50 dark:border-white/[0.08] overflow-hidden bg-muted/30 dark:bg-[#0f1011]">
          {slidesLoading || slides?.status === 'processing' || slides?.status === 'pending' ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <LoadingSpinner size="lg" text="Rendering slides..." />
              <p className="text-muted-foreground dark:text-[#8a8f98] mt-3" style={{ fontSize: '12px', letterSpacing: '-0.12px' }}>
                First view of a deck takes ~30s — the result is cached for future visits.
              </p>
            </div>
          ) : slides?.status === 'failed' ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                <FileText className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
              </div>
              <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Slide preview failed to render</p>
              {slides.error && (
                <p className="text-muted-foreground dark:text-[#8a8f98] mt-1 max-w-md" style={{ fontSize: '12px', letterSpacing: '-0.12px' }}>{slides.error}</p>
              )}
              {/* Bumping the nonce restarts the polling effect, whose first
                  call passes ?retry=true so the backend re-enqueues the render
                  (a plain open never re-renders a failed deck). */}
              <button
                type="button"
                onClick={() => setSlidesRetryNonce((n) => n + 1)}
                className="mt-4 px-3 py-1.5 rounded-md text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-[#5e6ad2]/10 dark:hover:bg-[#5e6ad2]/[0.15] transition-colors duration-150"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                Try again
              </button>
            </div>
          ) : slides && slides.slides?.length > 0 ? (
            <div>
              {/* Rendered slide image. The /slides response gives us
                  SAS URLs (or inline data URLs in dev) pointing at
                  Azure-served WebPs — <img src> renders them directly,
                  no proxy fetch. */}
              <div className="flex items-center justify-center bg-muted/30 dark:bg-[#0f1011] p-4">
                <img
                  src={slides.slides[currentSlide]?.image}
                  alt={`Slide ${currentSlide + 1}`}
                  className="w-full rounded-md shadow-lg"
                  style={{ maxHeight: 'calc(100vh - 400px)' }}
                />
              </div>

              {/* Slide navigation */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-background/60 border-t border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06]">
                <button
                  disabled={currentSlide === 0}
                  onClick={() => setCurrentSlide(s => s - 1)}
                  className="px-3 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 dark:text-[#d0d6e0] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  Previous
                </button>
                <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  <span className="text-foreground dark:text-[#f7f8f8] tabular-nums">{currentSlide + 1}</span>
                  <span className="opacity-50 mx-1">/</span>
                  <span className="tabular-nums">{slides.total_slides}</span>
                </span>
                <button
                  disabled={currentSlide >= slides.total_slides - 1}
                  onClick={() => setCurrentSlide(s => s + 1)}
                  className="px-3 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 dark:text-[#d0d6e0] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  Next
                </button>
              </div>

              {/* Speaker notes */}
              {slides.slides[currentSlide]?.notes && (
                <div className="px-4 py-2.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.02]">
                  <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d] mb-1">Speaker Notes</p>
                  <p className="text-foreground/90 dark:text-[#d0d6e0]" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>{slides.slides[currentSlide].notes}</p>
                </div>
              )}

              {/* Slide thumbnails strip */}
              {slides.total_slides > 1 && (
                <div className="flex gap-2 px-4 py-2.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/40 dark:bg-white/[0.02] overflow-x-auto">
                  {slides.slides.map((s: any, i: number) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      className={cn(
                        'flex-shrink-0 rounded-md overflow-hidden border transition-colors duration-150',
                        i === currentSlide
                          ? 'border-[#5e6ad2] shadow-md shadow-[#5e6ad2]/20'
                          : 'border-border/60 dark:border-white/[0.06] opacity-60 hover:opacity-100'
                      )}
                    >
                      <img
                        src={s.image}
                        alt={`Slide ${i + 1}`}
                        className="h-12 w-auto"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                <FileText className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
              </div>
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Could not load slide preview</p>
            </div>
          )}
        </div>
      )}

      {/* Website preview — show source URL link */}
      {isWebsite && material.url && (
        <div className="rounded-lg border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] p-6 text-center space-y-3">
          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto">
            <Globe className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
          </div>
          <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Website content</p>
          <a
            href={material.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline transition-colors duration-150"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            <ArrowSquareOut className="h-3.5 w-3.5" />
            Open original page
          </a>
        </div>
      )}

      {/* Inline text preview (docx, txt, markdown) — render the full extracted
          document content in place. This shows exactly what is in the file
          (all of it, via the ExtractedTextView footer's "Show all" toggle),
          so users no longer have to download to read docx/txt files. */}
      {canPreviewText && text && (
        <ExtractedTextView text={text} />
      )}

      {/* Non-previewable files (docx w/o extracted text, excel, csv, ...) —
          show file info card. Text-based types that DID extract content render
          inline above and are excluded here. */}
      {!canPreviewInline && !canPreviewSlides && !isWebsite && !(canPreviewText && text) && material.file_name && (() => {
        const FileIcon = getContentTypeIcon(material.content_type)
        return (
          <div className="rounded-lg border border-border/50 dark:border-white/[0.08] dark:bg-white/[0.02] p-6 text-center space-y-3">
            <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto">
              <FileIcon className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
            </div>
            <div>
              <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}>{material.file_name}</p>
              {material.file_size != null && (
                <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{formatBytes(material.file_size)}</p>
              )}
            </div>
            <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
              Preview not available for {material.content_type.toUpperCase()} files
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadMaterial(material.id, material.file_name)}
            >
              <DownloadSimple className="h-3.5 w-3.5 mr-1.5" />
              Download to view
            </Button>
          </div>
        )
      })()}

      {/* AI Summary card (shown for non-inline-preview types) */}
      {!canPreviewInline && !canPreviewSlides && sanitizeMaterialSummary(material.ai_summary) && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn(EYEBROW, 'mb-2')}>
                AI Summary
              </h3>
              <p className="text-foreground dark:text-[#f7f8f8] leading-relaxed" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {sanitizeMaterialSummary(material.ai_summary)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Toggle to show extracted text (for types not already rendering it
          inline, e.g. PDF/excel/csv). Text-based types show content inline. */}
      {text && !canPreviewText && (
        <button
          type="button"
          onClick={() => setShowExtractedText(true)}
          className="w-full flex items-center justify-center gap-2 h-10 rounded-md border bg-white/[0] hover:bg-muted/50 dark:bg-white/[0.02] dark:hover:bg-white/[0.04] border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] text-muted-foreground hover:text-foreground dark:text-[#d0d6e0] dark:hover:text-[#f7f8f8] transition-colors duration-150"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          <FileText className="h-3.5 w-3.5" />
          View extracted text
        </button>
      )}

      {/* No content fallback */}
      {!text && !material.file_name && !isWebsite && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
            <FileText className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
          </div>
          <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No content available.</p>
        </div>
      )}
    </div>
  )
}

// Extracted text viewer (accessible via "View extracted text" button)
function ExtractedTextView({ text, onBack }: { text: string; onBack?: () => void }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showAll, setShowAll] = useState(false)

  const lines = useMemo(() => text.split('\n'), [text])
  const displayLines = showAll ? lines : lines.slice(0, 500)
  const hasMore = lines.length > 500

  const wordCount = useMemo(() => text.split(/\s+/).filter(Boolean).length, [text])
  const charCount = text.length

  const highlightText = useCallback((line: string, query: string) => {
    if (!query.trim()) return line
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = line.split(regex)
    return parts.map((part) =>
      regex.test(part)
        ? `<mark class="bg-yellow-200 dark:bg-yellow-800 text-foreground rounded px-0.5">${part}</mark>`
        : part
    ).join('')
  }, [])

  const matchCount = useMemo(() => {
    if (!searchQuery.trim()) return 0
    const regex = new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    return (text.match(regex) || []).length
  }, [text, searchQuery])

  return (
    <div className="space-y-3">
      {/* Back button — only when used as a drilled-in view (onBack provided).
          When rendered inline as the primary preview there's nothing to go
          back to, so it is omitted. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-150"
          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
        >
          <CaretUp className="h-3.5 w-3.5 rotate-[-90deg]" weight="bold" />
          Back to preview
        </button>
      )}

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search extracted content..."
          className="w-full h-10 pl-10 pr-24 rounded-md bg-background border border-border/60 hover:border-border/80 focus-visible:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.03] dark:hover:border-white/[0.14] dark:focus-visible:border-[#7170ff]/75 outline-none transition-colors duration-150 text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d]"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        />
        {searchQuery && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d]">
            {matchCount} match{matchCount !== 1 ? 'es' : ''}
          </span>
        )}
      </div>

      {/* Text viewer */}
      <div className="rounded-lg bg-muted/20 border border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08] overflow-hidden">
        <div className="max-h-[calc(100vh-380px)] overflow-y-auto overflow-x-auto">
          <table className="w-full">
            <tbody className="font-mono text-[12px]">
              {displayLines.map((line, i) => (
                <tr key={i} className="hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-150">
                  <td className="select-none text-right pr-3 pl-3 py-px text-muted-foreground/40 dark:text-[#3e3e44] w-10 align-top border-r border-border/40 dark:border-white/[0.05]">
                    {i + 1}
                  </td>
                  <td className="pl-3 pr-4 py-px whitespace-pre-wrap break-words text-foreground/90 dark:text-[#d0d6e0]">
                    {searchQuery ? (
                      <span dangerouslySetInnerHTML={{ __html: highlightText(line, searchQuery) }} />
                    ) : (
                      line || '\u00A0'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/30 dark:bg-white/[0.02]">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d]">
            <span className="tabular-nums">{lines.length.toLocaleString()} lines</span>
            <span className="tabular-nums">{wordCount.toLocaleString()} words</span>
            <span className="tabular-nums">{charCount.toLocaleString()} chars</span>
          </div>
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline transition-colors duration-150"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              {showAll ? 'Show first 500 lines' : `Show all ${lines.length.toLocaleString()} lines`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Analysis Tab
// ---------------------------------------------------------------------------

function AnalysisTab({ material }: { material: MaterialDetail }) {
  const hasAnalysis = material.ai_summary || (material.key_points && material.key_points.length > 0)

  if (!hasAnalysis) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
          <Sparkle className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
        </div>
        <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}>No AI analysis available yet.</p>
        <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>Click "Re-analyze" to generate insights.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary — sanitized so legacy raw-JSON analyses never render (21572) */}
      {sanitizeMaterialSummary(material.ai_summary) && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Quotes className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn(EYEBROW, 'mb-2')}>
                AI Summary
              </h3>
              <p className="text-foreground dark:text-[#f7f8f8] leading-relaxed" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {sanitizeMaterialSummary(material.ai_summary)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Key Points */}
      {material.key_points && material.key_points.length > 0 && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
            <CheckCircle className="h-3.5 w-3.5" />
            Key Points
          </h3>
          <ul className="space-y-2.5">
            {material.key_points.map((point, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/[0.12] border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400" weight="bold" />
                </div>
                <p className="text-foreground dark:text-[#d0d6e0] leading-relaxed" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{point}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Category & Topics */}
      {(material.category || (material.topics && material.topics.length > 0)) && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
            <TagIcon className="h-3.5 w-3.5" />
            Classification
          </h3>
          <div className="flex flex-wrap gap-2">
            {material.category && (
              <span className="px-2.5 py-0.5 rounded-full border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40" style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.02em' }}>
                {material.category.replace(/_/g, ' ')}
              </span>
            )}
            {material.topics?.map((topic, i) => (
              <span
                key={i}
                className="px-2.5 py-0.5 rounded-full bg-muted/60 dark:bg-white/[0.04] border border-border/50 dark:border-white/[0.06] text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510 }}
              >
                {topic}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Competitive Insights — shown when the processing pipeline extracted
          any (the old company/competitor material-type gate was removed). */}
      {(material.competitive_insights || material.competitor_name) && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
            <ChartBar className="h-3.5 w-3.5" />
            Competitive Intelligence
          </h3>
          {material.competitor_name && (
            <div className="mb-2 flex items-baseline gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d]">Competitor</span>
              <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{material.competitor_name}</span>
              {material.competitor_industry && (
                <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>({material.competitor_industry})</span>
              )}
            </div>
          )}
          {material.competitive_insights ? (
            <pre className="font-mono text-[12px] bg-muted/30 dark:bg-white/[0.02] rounded-md p-3.5 overflow-auto max-h-60 text-foreground/80 dark:text-[#d0d6e0] border border-border/50 dark:border-white/[0.06]">
              {JSON.stringify(material.competitive_insights, null, 2)}
            </pre>
          ) : (
            <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>No competitive insights extracted yet.</p>
          )}
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metadata Tab
// ---------------------------------------------------------------------------

function MetadataTab({ material }: { material: MaterialDetail }) {
  const infoRows: { label: string; value: string | number | undefined; icon: typeof File }[] = [
    { label: 'File Name', value: material.file_name, icon: File },
    { label: 'Content Type', value: material.content_type, icon: StackSimple },
    { label: 'File Size', value: material.file_size != null ? formatBytes(material.file_size) : undefined, icon: Database },
    { label: 'Uploaded', value: material.created_at ? formatDate(material.created_at) : undefined, icon: Calendar },
    { label: 'Last Updated', value: material.updated_at ? formatDate(material.updated_at) : undefined, icon: Clock },
    { label: 'Usage Count', value: material.usage_count, icon: Hash },
  ]

  return (
    <div className="space-y-4">
      {/* File Info */}
      <Card variant="flat" padding="default" className={cardChromeClass}>
        <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
          <Info className="h-3.5 w-3.5" />
          File Information
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {infoRows.map(({ label, value, icon: Icon }) =>
            value != null && value !== '' && value !== 0 ? (
              <div key={label} className="p-3 bg-muted/40 dark:bg-white/[0.04] rounded-lg border border-border/50 dark:border-white/[0.06]">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="h-3 w-3 text-muted-foreground/70 dark:text-[#62666d]" />
                  <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d]">{label}</p>
                </div>
                <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{String(value)}</p>
              </div>
            ) : null
          )}
        </div>
      </Card>

      {/* URL */}
      {material.url && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <h3 className={cn(EYEBROW, 'mb-2 flex items-center gap-2')}>
            <Globe className="h-3.5 w-3.5" />
            Source URL
          </h3>
          <a
            href={material.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline inline-flex items-center gap-1.5 break-all transition-colors duration-150"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            {material.url}
            <ArrowSquareOut className="h-3 w-3 flex-shrink-0" />
          </a>
        </Card>
      )}

      {/* Processing Info */}
      <Card variant="flat" padding="default" className={cardChromeClass}>
        <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
          <ArrowsClockwise className="h-3.5 w-3.5" />
          Processing
        </h3>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>Status</span>
            <span className={cn(
              'px-2 py-0.5 rounded-full border uppercase tracking-[0.06em]',
              material.processing_status === 'completed' && 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:bg-emerald-500/[0.10] dark:border-emerald-500/25 dark:text-emerald-300',
              material.processing_status === 'failed' && 'bg-red-500/10 border-red-500/20 text-red-700 dark:bg-red-500/[0.10] dark:border-red-500/25 dark:text-red-300',
              !['completed', 'failed'].includes(material.processing_status) && 'bg-[#5e6ad2]/10 border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]',
            )} style={{ fontSize: '10px', fontWeight: 510 }}>
              {material.processing_status}
            </span>
          </div>
          {material.processed_at && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>Processed at</span>
              <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{formatDate(material.processed_at)}</span>
            </div>
          )}
          {material.processing_error && (
            <div className="mt-2 p-3 rounded-md bg-red-500/10 dark:bg-red-500/[0.10] border border-red-500/25 dark:border-red-500/25">
              <p className="text-red-700 dark:text-red-300" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{material.processing_error}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Extracted Metadata */}
      {material.extracted_metadata && Object.keys(material.extracted_metadata).length > 0 && (
        <Card variant="flat" padding="default" className={cardChromeClass}>
          <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
            <StackSimple className="h-3.5 w-3.5" />
            Document Metadata
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(material.extracted_metadata)
              .filter(([k]) => !['crawled_urls'].includes(k))
              .map(([key, value]) => (
              <div key={key} className="p-2.5 bg-muted/40 dark:bg-white/[0.04] rounded-lg border border-border/50 dark:border-white/[0.06]">
                <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d] capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-foreground dark:text-[#f7f8f8] truncate mt-0.5" style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tags — editable */}
      <TagEditor materialId={material.id} tags={material.tags || []} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tag Editor
// ---------------------------------------------------------------------------

function TagEditor({ materialId, tags: initialTags }: { materialId: number; tags: string[] }) {
  const [tags, setTags] = useState<string[]>(initialTags)
  const [inputValue, setInputValue] = useState('')
  const [saving, setSaving] = useState(false)
  const queryClient = useQueryClient()

  // Sync when props change
  useEffect(() => {
    setTags(initialTags)
  }, [initialTags])

  const saveTags = async (newTags: string[]) => {
    setSaving(true)
    try {
      await materialsApi.update(materialId, { tags: newTags })
      setTags(newTags)
      queryClient.invalidateQueries({ queryKey: ['materials'] })
      queryClient.invalidateQueries({ queryKey: ['material-detail', materialId] })
    } catch (error) {
      // Revert to the last server-confirmed tags — never leave the chip row
      // showing an add/remove the API rejected — and say so, otherwise the
      // tag just disappears with no explanation.
      setTags(initialTags)
      toast.error('Could not save tags', {
        description: getErrorMessage(error, 'The change was reverted. Please try again.'),
      })
    } finally {
      setSaving(false)
    }
  }

  const addTag = () => {
    const val = inputValue.trim().toLowerCase()
    if (!val || tags.includes(val)) {
      setInputValue('')
      return
    }
    const newTags = [...tags, val]
    setInputValue('')
    saveTags(newTags)
  }

  const removeTag = (tag: string) => {
    saveTags(tags.filter(t => t !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  return (
    <Card variant="flat" padding="default" className={cardChromeClass}>
      <h3 className={cn(EYEBROW, 'mb-3 flex items-center gap-2')}>
        <TagIcon className="h-3.5 w-3.5" />
        Tags
      </h3>
      <div className="flex flex-wrap gap-2 mb-3">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-[#5e6ad2]/[0.10] text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40"
            style={{ fontSize: '11px', fontWeight: 510 }}
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="opacity-70 hover:opacity-100 hover:text-red-500 dark:hover:text-red-400 transition-colors duration-150"
              disabled={saving}
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-2.5 w-2.5" weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a tag..."
          className="flex-1 h-9 px-3 rounded-md bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 outline-none transition-colors duration-150 text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d]"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          disabled={saving}
        />
        <button
          onClick={addTag}
          disabled={!inputValue.trim() || saving}
          className="h-9 px-4 rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0] disabled:opacity-40 disabled:hover:bg-[#5e6ad2] transition-colors duration-150"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          Add
        </button>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main Drawer
// ---------------------------------------------------------------------------

const TABS: { id: TabId; label: string; icon: typeof Eye }[] = [
  { id: 'content', label: 'Content', icon: Eye },
  { id: 'analysis', label: 'AI Analysis', icon: Sparkle },
  { id: 'metadata', label: 'File Details', icon: Info },
]

export function MaterialPreviewDrawer({
  materialId,
  isOpen,
  onClose,
  onDelete,
  onAnalyze,
}: MaterialPreviewDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabId>('content')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Fetch full detail
  const { data: material, isLoading } = useQuery<MaterialDetail>({
    queryKey: ['material-detail', materialId],
    queryFn: async () => {
      const res = await materialsApi.getDetail(materialId!)
      return res.data
    },
    enabled: !!materialId && isOpen,
  })

  // Reset tab when opening a new material
  useEffect(() => {
    setActiveTab('content')
  }, [materialId])

  useScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const HeaderIcon = material ? getContentTypeIcon(material.content_type) : File

  return createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] backdrop-blur-md">
            {/* Backdrop — blur-only per §45, covers scrollbar gutter per §29 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed top-0 left-0 w-screen h-screen"
              onClick={onClose}
              aria-hidden="true"
            />

            {/* Drawer */}
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className={cn(
                'fixed inset-y-0 right-0 z-10 flex flex-col shadow-2xl',
                'w-full md:w-[70vw] lg:w-[65vw] xl:w-[60vw]',
                'bg-card border-l border-border/60 dark:bg-[#0f1011] dark:border-white/[0.08]'
              )}
            >
              {isLoading || !material ? (
                <div className="flex-1 flex items-center justify-center">
                  <LoadingSpinner size="lg" text="Loading material..." />
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex-shrink-0 border-b border-border/50 dark:border-white/[0.06]">
                    <div className="flex items-center justify-between px-5 pt-5 pb-2 gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center flex-shrink-0">
                          <HeaderIcon className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h2
                            className="text-foreground dark:text-[#f7f8f8] truncate"
                            style={{ fontSize: '18px', fontWeight: 590, letterSpacing: '-0.216px', lineHeight: 1.33 }}
                          >
                            {material.title}
                          </h2>
                          {material.description && material.description !== `Uploaded file: ${material.file_name}` && (
                            <p
                              className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                              style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                            >
                              {material.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={onClose}
                        className="text-muted-foreground hover:text-foreground hover:bg-muted/50 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] rounded-md transition-colors duration-150 p-1.5 flex-shrink-0"
                        aria-label="Close"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Status row */}
                    <div className="flex items-center gap-2 px-5 pb-3 flex-wrap">
                      {(() => {
                        const cfg = statusConfig[material.processing_status] || statusConfig.pending
                        const StatusIcon = cfg.icon
                        const isComplete = material.processing_status === 'completed'
                        const isFailed = material.processing_status === 'failed'
                        return (
                          <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border uppercase tracking-[0.06em]',
                            isComplete && 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:bg-emerald-500/[0.10] dark:border-emerald-500/25 dark:text-emerald-300',
                            isFailed && 'bg-red-500/10 border-red-500/20 text-red-700 dark:bg-red-500/[0.10] dark:border-red-500/25 dark:text-red-300',
                            !isComplete && !isFailed && 'bg-[#5e6ad2]/10 border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]',
                          )} style={{ fontSize: '10px', fontWeight: 510 }}>
                            <StatusIcon className={cn('h-3 w-3', ['processing', 'crawling', 'indexing'].includes(material.processing_status) && 'animate-spin')} />
                            {cfg.label}
                          </span>
                        )
                      })()}

                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-muted/60 border-border/50 text-muted-foreground dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 510 }}>
                        {material.content_type}
                      </span>

                      <div className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground dark:text-[#62666d]">
                        {/* `!= null`, not truthiness: a 0-byte upload / 0-page crawl is a
                            real value, and `&&` would render a bare "0" into this row. */}
                        {material.file_size != null && <span className="tabular-nums">{formatBytes(material.file_size)}</span>}
                        {material.crawl_pages_found != null && <span className="tabular-nums">{material.crawl_pages_found} pages</span>}
                        <span className="tabular-nums">{formatDate(material.created_at)}</span>
                      </div>
                    </div>

                    {/* Tab Bar */}
                    <div
                      className="flex px-5 gap-1"
                      role="tablist"
                      aria-label="Material details"
                      onKeyDown={(e) =>
                        handleTabListKeyDown(e, {
                          activeIndex: TABS.findIndex(t => t.id === activeTab),
                          count: TABS.length,
                          onSelect: (i) => setActiveTab(TABS[i].id),
                        })
                      }
                    >
                      {TABS.map(tab => {
                        const TabIcon = tab.icon
                        const active = activeTab === tab.id
                        return (
                          <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            id={`material-tab-${tab.id}`}
                            aria-selected={active}
                            aria-controls={`material-panel-${tab.id}`}
                            tabIndex={active ? 0 : -1}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                              'flex items-center gap-1.5 px-4 py-2 transition-colors duration-150 border-b-2 -mb-px',
                              active
                                ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#a5b4fc] dark:text-[#a5b4fc]'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border/80 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:border-white/[0.14]'
                            )}
                            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                          >
                            <TabIcon className="h-3.5 w-3.5" />
                            {tab.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Tab Content — this is already the scroll container, so it
                      carries the tabpanel role (and tabIndex so keyboard users
                      can scroll it) rather than a redundant wrapper. */}
                  <div
                    className="flex-1 overflow-y-auto p-5"
                    role="tabpanel"
                    id={`material-panel-${activeTab}`}
                    aria-labelledby={`material-tab-${activeTab}`}
                    tabIndex={0}
                  >
                    {activeTab === 'content' && <ContentTab material={material} />}
                    {activeTab === 'analysis' && <AnalysisTab material={material} />}
                    {activeTab === 'metadata' && <MetadataTab material={material} />}
                  </div>

                  {/* Footer */}
                  <div className="flex-shrink-0 border-t border-border/50 dark:border-white/[0.06] px-5 py-3.5 flex items-center justify-between bg-card/85 backdrop-blur-md dark:bg-[#0f1011]/85">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </Button>

                    <div className="flex items-center gap-2">
                      {material.file_path && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadMaterial(material.id, material.file_name)}
                        >
                          <DownloadSimple className="h-3.5 w-3.5 mr-1.5" />
                          Download
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAnalyze(material.id)}
                        className="bg-[#5e6ad2]/[0.08] border-[#5e6ad2]/25 text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.14] hover:border-[#5e6ad2]/40 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.20] dark:hover:border-[#5e6ad2]/45"
                      >
                        <Robot className="h-3.5 w-3.5 mr-1.5" />
                        Re-analyze
                      </Button>
                      <Button variant="outline" size="sm" onClick={onClose}>
                        Close
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete material"
        description={
          <>
            Are you sure you want to delete <span className="text-foreground dark:text-[#f7f8f8] font-medium">{material?.title || 'this material'}</span>? This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={() => {
          if (material) {
            onDelete(material.id)
            setShowDeleteConfirm(false)
            onClose()
          }
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>,
    document.body,
  )
}
